/**
 * Sidekick v3 motion.
 *
 * Doctrine (D2C Homepage Research doc, section 2.7), for a market that is 82% mobile:
 *   - Fade + 16px rise on scroll entry. Fires ONCE, never re-triggers.
 *   - IntersectionObserver only. Never a scroll event listener.
 *   - Total animation JS under 15KB gzipped.
 *
 * The hero deliberately uses none of this. Its entrance is pure CSS with animation-delay,
 * because above-the-fold copy must not wait on a script to become visible.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- reveal */

  function reveal(el) {
    el.classList.add('is-revealed');
  }

  function initReveal() {
    var targets = document.querySelectorAll('[data-anim] .anim, [data-anim].anim');
    if (!targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, reveal);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target); // fires once
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    Array.prototype.forEach.call(targets, function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------ accordion */
  /**
   * Exclusive, animated <details> groups.
   *
   * <details> cannot animate its own height, and the native `name` attribute that gives
   * exclusivity is not supported widely enough to rely on for mobile India. So the open
   * state is driven here instead, while the markup stays <details>/<summary> so it remains
   * keyboard accessible and its content stays in the DOM for indexing.
   *
   * Height is animated rather than transform because an accordion's animation IS its height.
   * It is confined to one element and settles in 260ms, which is the honest exception to the
   * transform/opacity rule.
   */
  var DUR = 260;
  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function panelOf(d) {
    return d.querySelector('[data-panel]');
  }

  /**
   * Two things that bit here and are worth keeping written down:
   *
   * 1. Reading panel.scrollHeight in the same tick as setAttribute('open') returns just the
   *    panel's own padding and border, because <details> has not laid out its children yet.
   *    Measuring inside requestAnimationFrame is what makes the open animation correct.
   * 2. Animation.onfinish is not guaranteed to fire if the animation is cancelled or the tab
   *    is backgrounded mid-flight, which would leave a card stuck open. Every finish handler
   *    therefore also has a timeout fallback, and both are written to be idempotent.
   */
  function settle(fn) {
    var ran = false;
    return function () {
      if (ran) return;
      ran = true;
      fn();
    };
  }

  function expand(d) {
    var panel = panelOf(d);
    d.setAttribute('open', '');
    if (!panel || reduced.matches) return;

    requestAnimationFrame(function () {
      var target = panel.scrollHeight;
      if (!target) return;
      panel.style.overflow = 'hidden';
      var done = settle(function () {
        panel.style.height = '';
        panel.style.overflow = '';
      });
      panel.animate(
        [{ height: '0px' }, { height: target + 'px' }],
        { duration: DUR, easing: EASE }
      ).onfinish = done;
      setTimeout(done, DUR + 80);
    });
  }

  function collapse(d) {
    var panel = panelOf(d);
    if (!panel || reduced.matches) {
      d.removeAttribute('open');
      return;
    }

    var start = panel.scrollHeight;
    panel.style.overflow = 'hidden';
    var done = settle(function () {
      d.removeAttribute('open');
      panel.style.height = '';
      panel.style.overflow = '';
    });
    panel.animate(
      [{ height: start + 'px' }, { height: '0px' }],
      { duration: DUR, easing: EASE }
    ).onfinish = done;
    setTimeout(done, DUR + 80);
  }

  function initAccordions() {
    var groups = document.querySelectorAll('[data-accordion]');
    if (!groups.length) return;

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.accordionReady) return;
      group.dataset.accordionReady = '1';

      var items = Array.prototype.slice.call(group.querySelectorAll('details'));

      items.forEach(function (d) {
        var summary = d.querySelector('summary');
        if (!summary) return;

        summary.addEventListener('click', function (e) {
          // Take over from the browser so open and close can both be animated.
          e.preventDefault();

          if (d.hasAttribute('open')) {
            collapse(d);
            return;
          }

          // Exclusive: close whatever else is open first.
          items.forEach(function (other) {
            if (other !== d && other.hasAttribute('open')) collapse(other);
          });

          expand(d);
        });
      });
    });
  }

  /* ----------------------------------------------------------------- tabs */
  /**
   * Symptom grid for the problem router.
   *
   * Every panel is server-rendered and stays in the DOM, hidden with the `hidden` attribute.
   * Nothing here creates content, so the explanatory copy is indexable whether or not this
   * script runs, and with JS off the first panel is simply the one that shows.
   *
   * Arrow keys move between tabs because a role="tablist" that only responds to clicks is
   * worse than no role at all: it promises keyboard behaviour to a screen reader and then
   * does not deliver it.
   */
  function initTabs() {
    var lists = document.querySelectorAll('[data-tabs]');
    if (!lists.length) return;

    Array.prototype.forEach.call(lists, function (list) {
      if (list.dataset.tabsReady) return;
      list.dataset.tabsReady = '1';

      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function select(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(tab, false);
        });

        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') next = tabs[0];
          else if (e.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          e.preventDefault();
          select(next, true);
        });
      });
    });
  }

  /* ----------------------------------------------------- comparison drawings */
  /**
   * Toggles `.is-live` on the comparison section, which tips the no-camber drawing over and
   * shakes the cambered one to show it holds. All the motion itself is CSS.
   *
   * Unlike the reveals above, this one does NOT unobserve. The class comes off on exit so the
   * chair stands back up, and the demonstration replays next time it is scrolled past.
   */
  function initCompare() {
    var stages = document.querySelectorAll('[data-cmp-stage]');
    if (!stages.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) return;

    // Watches the DRAWINGS, not the section. The section is over 1000px tall, so observing it
    // fired the topple while the drawings were still below the fold and the demonstration
    // played to nobody.
    //
    // `threshold: 1` is the obvious way to say "fully visible" and it does not work: a stage
    // whose height lands on a fractional pixel reports a ratio of 0.9998 and the callback
    // never fires. Hence a band of thresholds and an explicit tolerance.
    var FULL = 0.96;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var section = entry.target.closest('[data-cmp]');
          if (section) section.classList.toggle('is-live', entry.intersectionRatio >= FULL);
        });
      },
      { rootMargin: '-32px 0px -32px 0px', threshold: [0, 0.5, FULL, 1] }
    );

    Array.prototype.forEach.call(stages, function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------- header state */
  /**
   * On the homepage the header floats transparent over the hero video and takes on a cream
   * glass background once the video panel has gone by.
   *
   * No scroll listener, per the doctrine at the top of this file.
   */

  // How far below the viewport top the hero's video panel must sit before the bar takes on a
  // background. It is not zero, because the bottom of the hero is a cream fade into the next
  // section: leaving the swap until the fade cleared the top meant white nav links spent about
  // 130px of scroll sitting on cream, invisible. On mobile it was worse, because the old
  // sentinel was at the foot of the whole SECTION, well below the 470px video panel, so the
  // bar stayed transparent over the copy block too.
  var HEADER_SWAP_AT = 200;

  function initHeader() {
    var header = document.querySelector('[data-header].header--over-hero');
    // The video panel, not the section and not the sentinel at its foot.
    var target = document.querySelector('.hero-video .hero__media') ||
                 document.querySelector('[data-hero-end]');
    if (!header) return;

    // Any page that claims the over-hero header but has no hero must not be left with an
    // invisible bar over its content.
    if (!target || !('IntersectionObserver' in window)) {
      header.classList.add('is-solid');
      return;
    }

    // The test is a measured edge, NOT `isIntersecting`. The target is out of view both before
    // the hero is reached and after it is scrolled past, so isIntersecting reads false in both
    // cases and cannot tell them apart; using it put the bar solid at scroll 0, over the video.
    //
    // rootMargin pulls the root's top edge down by the same amount, which is what makes the
    // callback actually fire at the swap point. Do NOT collapse the root to a line
    // ('0px 0px -100% 0px'): a zero-height root can never be intersected, so the state never
    // changes and the observer goes permanently silent after its first callback.
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          header.classList.toggle('is-solid', entry.boundingClientRect.bottom <= HEADER_SWAP_AT);
        });
      },
      { rootMargin: '-' + HEADER_SWAP_AT + 'px 0px 0px 0px', threshold: 0 }
    ).observe(target);
  }

  /* ----------------------------------------------------------------- boot */

  function init() {
    initReveal();
    initAccordions();
    initTabs();
    initCompare();
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify's theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
